import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  GKButton,
  GKClientDataGrid,
  Grid2,
  IconButton,
  PlusIcon,
  SendIcon,
  StarFillIcon,
  StarIcon,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, refetchQuery } from "utils/helpers";
import { removeUnderscoreAndFirstLetterCapital } from "utils/StringFunctions";
import EmailTemplateEditorModal from "./EmailTemplateEditorModal";
import SendEmailTemplate from "./SendEmailTemplate";

interface EmailTemplate {
  id: string;
  subject?: string;
  file?: File;
  isDefault?: boolean;
  editable: boolean;
  gridkeyTemplate: boolean;
}

const EmailTemplates = () => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const {
    data: Templates,
    isLoading: isTemplatesLoading,
    refetch,
  } = useFetchQuery({
    key: ["GET_EMAIL_TEMPLATES"],
    route: "/email-templates/",
  });

  const { mutate: handleMakeDefault } = useMutation({
    mutationFn: (templateId: string) =>
      axiosInstance.patch(`/email-templates/${templateId}/make-default/`),
    onSuccess: () => {
      refetchQuery("GET_EMAIL_TEMPLATES");
    },
  });

  const { mutate: handleDelete } = useMutation({
    mutationFn: (templateId: string) =>
      axiosInstance.delete(`/email-templates/${templateId}/`),
    onSuccess: () => {
      refetchQuery("GET_EMAIL_TEMPLATES");
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Template Name", "subject", 180, 1),
    },
    {
      ...extraColumnsField("Email Type", "emailCategoryStr", 150, 1),
      valueFormatter: (params) =>
        removeUnderscoreAndFirstLetterCapital(params?.value),
    },
    {
      ...extraColumnsField("Action", "action", 100, 1),
      align: "right",
      headerAlign: "right",
      type: "action",
      renderCell: (params: any) => {
        return (
          <Grid2 container justifyContent={"flex-end"} gap={1} display={"flex"}>
            {params.row.emailCategoryStr === "general" && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTemplate(params.row);
                  setIsSendModalOpen(true);
                }}
              >
                <SendIcon width={20} height={20} />
              </IconButton>
            )}
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleMakeDefault(params.row.id);
                refetch();
              }}
            >
              {params.row.isDefault ? (
                <StarFillIcon
                  width={16}
                  height={16}
                  color={"var(--primary-900)"}
                />
              ) : (
                <StarIcon width={16} height={16} color={"var(--primary-900)"} />
              )}
            </IconButton>
            <IconButton
              color="error"
              disabled={params.row.editable === false}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTemplate(params.row);
                handleDelete(params.row.id);
              }}
            >
              <DeleteIcon width={18} />
            </IconButton>
          </Grid2>
        );
      },
    },
  ];

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={12}>
        <GKClientDataGrid
          name="emailTemplates"
          columns={columns}
          rows={Templates?.data?.data || []}
          onRowClick={(row) => {
            setSelectedTemplate(row.row);
            setIsEditorOpen(true);
          }}
          loading={isTemplatesLoading}
          headerComponent={
            <GKButton
              onClick={() => {
                setSelectedTemplate(null);
                setIsEditorOpen(true);
              }}
              variant="contained"
              size="small"
              startIcon={<PlusIcon width={18} />}
            >
              Add Template
            </GKButton>
          }
        />
      </Grid2>

      {isEditorOpen && (
        <EmailTemplateEditorModal
          open={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          template={selectedTemplate}
        />
      )}
      {isSendModalOpen && (
        <SendEmailTemplate
          open={isSendModalOpen}
          onClose={() => setIsSendModalOpen(false)}
          templateId={selectedTemplate?.id}
        />
      )}
    </Grid2>
  );
};

export default EmailTemplates;
